import { JsonObject } from '@bufbuild/protobuf';
import {
  ConnType,
  CredentialItem,
  CredentialItem_CredType,
  DriverType,
  Source,
  Source_CommunicationInfo_Type,
  Source_State,
  Source_Type,
  UrlItem,
} from '@q6cyber/proto2es/generated/source';

import {
  AbstractControl,
  FormControl,
  FormGroup,
  ValidationErrors,
  Validators,
} from '@angular/forms';

import { RouterOutput } from '../../../utils';
import { CustomFormArray, customFormGroup } from '../utilities';
import {
  cryptoAddressValidator,
  enumDefaultValidator,
  isTLDDomainTemplate,
} from '../validations';
import { ChipsAutocomplete } from './autocomplete.interface';
import { commentsForm } from './comments.interface';
import { CorruptedData } from './generic-data.interface';
import { moneyForm } from './money.interface';
import { physicalAddressForm } from './physical-address.interface';
import { sourceTagGroupsForm } from './source-tags.interface';

/** A row of the Sources list. `node` is nullable so one malformed record
 * renders as a corrupted row instead of breaking the list. */
export interface SourcesListResponse
  extends
    Omit<RouterOutput['sources']['list']['edges'][0], 'node'>,
    CorruptedData {
  node: Source | null;
}

/** `optionList` stays undefined until suggestions arrive, so a new-value hint
 *  never fires against suggestions that have not loaded. */
export const freeTextAutocomplete = (
  optionList?: string[]
): ChipsAutocomplete => ({
  freeText: true,
  ...(optionList ? { optionList } : {}),
});

/** `sources.valueList` keyed by field path; `null` until it has loaded. */
export type SourceFieldSuggestions =
  RouterOutput['sources']['valueList'] | null;

/** State and type are multi-selects; an empty list means no filter. */
export interface SourcesFilters {
  search: string;
  state: string[];
  type: string[];
  reaperActive: boolean;
}

export const DEFAULT_SOURCES_FILTERS: SourcesFilters = {
  search: '',
  state: [Source_State[Source_State.ACTIVE]],
  type: [],
  reaperActive: false,
};

/** `firstIdentified` is seeded per add instead, so a module-level constant
 *  cannot freeze it at load time. */
export const DEFAULT_SOURCE = new Source()
  .fromJson({
    state: Source_State[Source_State.ACTIVE],
    type: Source_Type[Source_Type.SHOP],
  })
  .toJson({ emitDefaultValues: true }) as JsonObject;

/** Dragonfly's defaults: a new credential starts valid and collectable. */
export const DEFAULT_SOURCE_CREDENTIAL = new CredentialItem()
  .fromJson({
    credType: CredentialItem_CredType[CredentialItem_CredType.REGULAR],
    connType: ConnType[ConnType.PROXY],
    driverType: DriverType[DriverType.Firefox],
  })
  .toJson({ emitDefaultValues: true }) as JsonObject;

/** Dragonfly's defaults: a new URL starts active with a proxy and driver. */
export const DEFAULT_SOURCE_URL = new UrlItem()
  .fromJson({
    isActive: true,
    connType: ConnType[ConnType.PROXY],
    driverType: DriverType[DriverType.Firefox],
    // A message field is only emitted once set; Dragonfly sent it all-false.
    settings: {},
  })
  .toJson({ emitDefaultValues: true }) as JsonObject;

/** These break downstream collection for SHOP sources. */
export const SOURCE_DISALLOWED_QUOTES = [
  '¨',
  '´',
  '`',
  '“',
  '”',
  '„',
  '‚',
  '‘',
  '’',
  "'",
  '"',
];

/** A subsource is named by `subSourceName`; it still carries the parent's
 * `sourceName`, which is why that control stays required in both cases. */
const subSourceNameValidator = (
  group: AbstractControl
): ValidationErrors | null => {
  const isSubsource = group.get('isSubsource')?.value;
  const subSourceName = group.get('subSourceName')?.value;
  return isSubsource && !subSourceName ? { subSourceNameRequired: true } : null;
};

/** Only enforced on create: existing SHOP names predate the rule. */
const shopNameQuotesValidator = (
  group: AbstractControl
): ValidationErrors | null => {
  const isNew = !group.get('id')?.value;
  const isShop = group.get('type')?.value === Source_Type[Source_Type.SHOP];

  if (!isNew || !isShop) {
    return null;
  }

  const names = [
    group.get('sourceName')?.value,
    group.get('subSourceName')?.value,
  ];
  const hasQuotes = names.some(name =>
    SOURCE_DISALLOWED_QUOTES.some(quote => (name ?? '').includes(quote))
  );

  return hasQuotes ? { shopNameQuotes: true } : null;
};

/** The `ElasticRef.lookup` variant every source reference on the index uses. */
export const SOURCE_DESCRIPTOR = 'source.Source';

/** `ElasticRef.lookup` is a oneof, so only one of the three may exist at a
 *  time. New refs pick the descriptor; loaded ones keep whatever they carry. */
export const sourceRefForm = () =>
  customFormGroup(
    { id: new FormControl('', Validators.required) },
    {
      conditionalControls: [
        {
          conditions: [
            {
              key: 'descriptor_name',
              factory: () => new FormControl(SOURCE_DESCRIPTOR),
            },
            { key: 'class_name', factory: () => new FormControl('') },
            { key: 'index_name', factory: () => new FormControl('') },
          ],
        },
      ],
    }
  );

export type SourceRefFormGroup = ReturnType<typeof sourceRefForm>;

export const selectSourceDescriptor = (ref: SourceRefFormGroup): void => {
  ref.selectConditionalControl('descriptor_name', SOURCE_DESCRIPTOR);
};

/**
 * A TA contact. `userDetails` and `url` are not edited here; they survive a
 * round trip as dynamic controls.
 */
export const sourceContactForm = (serviceRequired = false) =>
  customFormGroup(
    {
      service: new FormControl<string | null>(
        null,
        serviceRequired
          ? [
              Validators.required,
              enumDefaultValidator(Source_CommunicationInfo_Type),
            ]
          : []
      ),
      contact: new FormControl('', { nonNullable: true }),
    },
    {
      errorMessages: [
        {
          fieldKey: 'service',
          errorName: 'required',
          message: 'Service is required',
        },
      ],
    }
  );

export const sourceCommunityForm = () =>
  customFormGroup({
    community: sourceRefForm(),
    role: new FormControl<string | null>(null),
    contactInfo: sourceContactForm(),
    comments: new CustomFormArray([], () => commentsForm()),
  });

/** Either half of the oneof must name someone. */
const victimRequiredValidator = (
  group: AbstractControl
): ValidationErrors | null =>
  group.get('victimName')?.value || group.get('victimRef')?.get('id')?.value
    ? null
    : { victimRequired: true };

/** `victimInfo` is a proto oneof, so exactly one of the two keys may be
 *  present; the conditional set keeps the other control out of the payload. */
export const sourceVictimForm = () =>
  customFormGroup(
    { comments: new CustomFormArray([], () => commentsForm()) },
    {
      conditionalControls: [
        {
          conditions: [
            { key: 'victimName', factory: () => new FormControl('') },
            {
              key: 'victimRef',
              factory: () => new FormGroup({ id: new FormControl('') }),
            },
          ],
        },
      ],
    },
    { validators: [victimRequiredValidator] }
  );

export const sourceWalletForm = () =>
  customFormGroup(
    {
      cryptoCurrency: new FormControl<string | null>(null),
      walletId: new FormControl(''),
    },
    undefined,
    { validators: [cryptoAddressValidator()] }
  );

export const sourceTransactionForm = () =>
  customFormGroup({
    transactionId: new FormControl('', Validators.required),
    transaction: moneyForm(),
  });

export const sourceCredentialForm = () =>
  customFormGroup({
    disabled: new FormControl(false, { nonNullable: true }),
    username: new FormControl(''),
    password: new FormControl(''),
    credType: new FormControl<string | null>(null, Validators.required),
    notes: new FormControl(''),
    connType: new FormControl<string | null>(null),
    driverType: new FormControl<string | null>(null),
  });

/** Dragonfly's URL rule (a TLD domain, a Tor URL or an IP) under its message. */
const sourceUrlValidator = (
  control: AbstractControl
): ValidationErrors | null =>
  isTLDDomainTemplate(control) ? { invalidSourceUrl: true } : null;

export const sourceUrlForm = () =>
  customFormGroup({
    isActive: new FormControl(true, { nonNullable: true }),
    url: new FormControl('', [Validators.required, sourceUrlValidator]),
    connType: new FormControl<string | null>(null),
    driverType: new FormControl<string | null>(null),
    settings: new FormGroup({
      exactHostMatch: new FormControl(false, { nonNullable: true }),
      wildcardPath: new FormControl(false, { nonNullable: true }),
      wildcardParams: new FormControl(false, { nonNullable: true }),
    }),
  });

const CREDENTIAL_LOCKED_KEYS = [
  'username',
  'password',
  'credType',
  'notes',
  'connType',
  'driverType',
];
const URL_LOCKED_KEYS = ['url', 'connType', 'driverType', 'settings'];

const setLocked = (
  group: AbstractControl,
  keys: string[],
  locked: boolean,
  emitEvent: boolean
): void =>
  keys.forEach(key => {
    const control = group.get(key);
    if (locked) {
      control?.disable({ emitEvent });
    } else {
      control?.enable({ emitEvent });
    }
  });

/** An inactive credential is read-only apart from its own Active flag. */
export const syncCredentialLock = (
  group: AbstractControl,
  emitEvent = true
): void =>
  setLocked(
    group,
    CREDENTIAL_LOCKED_KEYS,
    !!group.get('disabled')?.value,
    emitEvent
  );

/** An inactive URL is read-only apart from its own Active flag. */
export const syncUrlLock = (group: AbstractControl, emitEvent = true): void =>
  setLocked(group, URL_LOCKED_KEYS, !group.get('isActive')?.value, emitEvent);

export const sourceAssociatedForm = () =>
  customFormGroup({
    reference: sourceRefForm(),
    comments: new CustomFormArray([], () => commentsForm()),
  });

export const sourceTaInfoForm = () =>
  customFormGroup({
    maliciousActivity: new FormControl<string[]>([], { nonNullable: true }),
    areaOfExpertise: new FormControl<string[]>([], { nonNullable: true }),
    internalTools: new FormControl<string[]>([], { nonNullable: true }),
    searchTerms: new FormControl<string[]>([], { nonNullable: true }),
    address: physicalAddressForm(),
    communications: new CustomFormArray([], () => sourceContactForm(true)),
    communities: new CustomFormArray([], () => sourceCommunityForm()),
    victims: new CustomFormArray([], () => sourceVictimForm()),
  });

export type SourceContactFormGroup = ReturnType<typeof sourceContactForm>;
export type SourceCommunityFormGroup = ReturnType<typeof sourceCommunityForm>;
export type SourceVictimFormGroup = ReturnType<typeof sourceVictimForm>;
export type SourceWalletFormGroup = ReturnType<typeof sourceWalletForm>;
export type SourceTransactionFormGroup = ReturnType<
  typeof sourceTransactionForm
>;
export type SourceCredentialFormGroup = ReturnType<typeof sourceCredentialForm>;
export type SourceUrlFormGroup = ReturnType<typeof sourceUrlForm>;
export type SourceAssociatedFormGroup = ReturnType<typeof sourceAssociatedForm>;
export type SourceTaInfoFormGroup = ReturnType<typeof sourceTaInfoForm>;
export type SourcesFormGroup = ReturnType<typeof sourcesForm>;

export const sourcesForm = () => {
  const form = customFormGroup(
    {
      id: new FormControl(''),
      sourceName: new FormControl('', [Validators.required]),
      subSourceName: new FormControl(''),
      sourceCoverName: new FormControl(''),
      isSubsource: new FormControl(false, { nonNullable: true }),
      parentSource: new FormControl(''),
      type: new FormControl<string | null>(null, [
        Validators.required,
        enumDefaultValidator(Source_Type),
      ]),
      state: new FormControl<string | null>(null),
      firstIdentified: new FormControl<string | null>(null),
      lastEditedBy: new FormControl(''),
      taInfo: sourceTaInfoForm(),
      associatedSources: new CustomFormArray([], () => sourceAssociatedForm()),
      wallets: new CustomFormArray([], () => sourceWalletForm()),
      transactions: new CustomFormArray([], () => sourceTransactionForm()),
      tagGroups: sourceTagGroupsForm(),
      credentials: new CustomFormArray([], () => sourceCredentialForm()),
      urlList: new CustomFormArray([], () => sourceUrlForm()),
      richComments: new CustomFormArray([], () => commentsForm()),
      keyPeopleRef: new FormControl<JsonObject[]>([], { nonNullable: true }),
    },
    {
      errorMessages: [
        {
          fieldKey: 'sourceName',
          errorName: 'required',
          message: 'Source name is required',
        },
        {
          fieldKey: 'type',
          errorName: 'required',
          message: 'Type is required',
        },
        {
          fieldKey: 'type',
          errorName: 'enumDefault',
          message: 'Type is required',
        },
      ],
    },
    { validators: [subSourceNameValidator, shopNameQuotesValidator] }
  );

  return form;
};
